import { useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import StandardModal, { ColorThemeName, PatientName } from 'components/shared/StandardModal';
import { ReviewStatus, ReviewType } from 'models/Review';
import CoordinateRequest from 'models/reviews/CoordinateRequest';
import { importedPatientsQueryKeys } from 'services/api/importedPatients';
import { coordinateRequestQueryKeys, updateReview } from 'services/api/reviews';
import { useToastActions } from 'stores/toastStore';
import colors from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

const REASON_MAX_LENGTH = 500;

type Props = {
  review: CoordinateRequest;
  onClose: () => void;
};

export default function DismissCoordinateRequestModal({ review, onClose }: Props) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const [reason, setReason] = useState('');

  const patientName = review.importedPatient?.name ?? 'this request';

  const { mutate, isPending } = useMutation({
    mutationFn: updateReview,
  });

  const handleConfirm = () => {
    mutate(
      {
        id: review.id,
        type: ReviewType.COORDINATE_REQUEST,
        status: ReviewStatus.REJECTED,
        denialReason: reason.trim() || undefined,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: coordinateRequestQueryKeys.base });
          queryClient.invalidateQueries({ queryKey: importedPatientsQueryKeys.index });
          addToast({ text: `Coordinate request for ${patientName} dismissed.` });
          onClose();
        },
        onError: () => {
          addToast({ text: 'Something went wrong. Please try again.' });
        },
      }
    );
  };

  return (
    <StandardModal colorTheme={ColorThemeName.PrimaryRed} onCancel={onClose} title='Dismiss Request'>
      <PatientName>Patient: {patientName}</PatientName>
      <Content>
        <BodySmall>
          Dismissing this request declines coordination and ends visibility into this patient&apos;s care. You will not
          receive updates from the provider. The provider will be notified, and a new request will be required to
          coordinate care.
        </BodySmall>
        <Label htmlFor='dismiss-reason'>Reason (optional)</Label>
        <TextArea
          id='dismiss-reason'
          placeholder='Add a reason for dismissing this request'
          value={reason}
          maxLength={REASON_MAX_LENGTH}
          onChange={(event) => setReason(event.target.value)}
        />
        <CharCount $atLimit={reason.length >= REASON_MAX_LENGTH}>
          {reason.length} / {REASON_MAX_LENGTH}
        </CharCount>
      </Content>
      <StyledButtonGroup>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='danger' onClick={handleConfirm} loading={isPending}>
          Dismiss Request
        </Button>
      </StyledButtonGroup>
    </StandardModal>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 16px 0 32px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.black75};
  margin-top: 8px;
`;

const TextArea = styled.textarea`
  border: 1px solid ${colors.black25};
  border-radius: 4px;
  font-size: 14px;
  line-height: 20px;
  min-height: 96px;
  padding: 12px 16px;
  resize: none;
  width: 100%;
  outline: none;
  background-color: transparent;

  &::placeholder {
    color: ${colors.black25};
  }
`;

const CharCount = styled.span<{ $atLimit: boolean }>`
  align-self: flex-end;
  color: ${({ $atLimit }) => ($atLimit ? colors.red100 : colors.black50)};
  font-size: 12px;
  margin-top: 4px;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  justify-content: flex-end;
`;
